/**
 * DEV-ONLY tutorial launcher.
 *
 * Registry guide walkthroughs normally arrive only from the AI chat (`ui_guide`
 * rich_content). This floating panel lets a developer start any walkthrough by
 * hand to test it — it renders nothing outside `import.meta.env.DEV`, so it
 * never ships to production. It also re-exposes the
 * `window.__startGuideWalkthrough(id)` console entrypoint.
 *
 * Placement: bottom-LEFT, to stay clear of the AI panel button (bottom-right).
 */
import { useEffect, useState } from "react";
import { GraduationCap, X } from "lucide-react";
import i18n from "@/lib/i18n";
import { GUIDE_WALKTHROUGHS } from "@/lib/guide/walkthroughs";
import { startGuideFromPayload } from "@/lib/guide/fromPayload";
import type { RichUiGuideContent } from "@/app/api/types/chat-sessions";

const IS_DEV = import.meta.env.DEV;

function startWalkthrough(walkthroughId: string): void {
  startGuideFromPayload(
    {
      type: "ui_guide",
      // Fresh id each time so the consumed-id dedupe never blocks a re-run.
      guide_id: `dev-${walkthroughId}-${Date.now()}`,
      walkthrough_id: walkthroughId,
    } as RichUiGuideContent,
    { replay: true },
  );
}

export function GuideDevLauncher() {
  const [open, setOpen] = useState(false);

  // Console entrypoint: `__startGuideWalkthrough("focus-transport-tour")`.
  useEffect(() => {
    if (!IS_DEV) return;
    (window as unknown as Record<string, unknown>).__startGuideWalkthrough =
      startWalkthrough;
    return () => {
      delete (window as unknown as Record<string, unknown>)
        .__startGuideWalkthrough;
    };
  }, []);

  if (!IS_DEV) return null;

  const t = i18n.t.bind(i18n);

  return (
    <div className="fixed bottom-4 left-4 z-[9998] flex flex-col items-start gap-2">
      {open && (
        <div className="w-72 rounded-xl border border-guide/30 bg-background p-2 shadow-xl">
          <div className="flex items-center justify-between px-1 pb-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-guide">
              Tutorials (dev)
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Close tutorial launcher"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <ul className="flex flex-col gap-1">
            {GUIDE_WALKTHROUGHS.map((walkthrough) => (
              <li key={walkthrough.id}>
                <button
                  type="button"
                  data-testid={`guide-dev-launch-${walkthrough.id}`}
                  onClick={() => startWalkthrough(walkthrough.id)}
                  className="w-full rounded-lg px-2 py-1.5 text-left hover:bg-guide/10"
                >
                  <div className="text-sm font-medium text-foreground">
                    {t(walkthrough.titleKey)}
                  </div>
                  <div className="font-mono text-[10px] text-muted-foreground">
                    {walkthrough.id}
                    {walkthrough.requires
                      ? ` · requires ${walkthrough.requires}`
                      : ""}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="guide-dev-launcher-toggle"
        title="Start a tutorial (dev only)"
        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-guide/30 bg-background text-guide shadow-lg hover:bg-guide/10"
      >
        <GraduationCap className="h-5 w-5" />
      </button>
    </div>
  );
}
